import axios from "axios";
import qs from "qs";

// Builds the export workbook in a Web Worker so a large export (tens of thousands of
// rows, as production data grows) doesn't freeze the tab while XLSX serializes it.
const buildPatientsWorkbookBuffer = (headers = [], rows = []) =>
  new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL("../workers/patientsExportWorkbook.worker.js", import.meta.url)
    );

    worker.onmessage = (event) => {
      const { ok, arrayBuffer, error } = event.data || {};
      worker.terminate();
      if (ok) {
        resolve(arrayBuffer);
      } else {
        reject(new Error(error || "Failed to build workbook"));
      }
    };

    worker.onerror = (error) => {
      worker.terminate();
      reject(error);
    };

    worker.postMessage({ headers, rows });
  });

const apiService = {
  fetchClinic: async (auth, entityId, page = 1, limit = 10, keyword = "") => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/listClinics/${entityId}`,
        {
          params: { page, limit, keyword },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );

      // The API returns { clinics: [...], totalItems: N }
      const clinics = response.data.data.clinics || [];
      const totalItems = response.data.data.totalItems || 0;
      // Map the data if needed (example: you can rename fields)
      const mappedClinics = clinics.map((clinic) => ({
        id: clinic._id,
        name: clinic.name,
        city: clinic.city,
        totalScans: clinic.totalScans || 0,
        doctorId: clinic.doctorId,
      }));

      return { clinics: mappedClinics, totalItems };
    } catch (error) {
      console.error("Error fetching clinics:", error);
      throw error;
    }
  },
  fetchDoctors: async (auth, entityId, page = 1, limit = 10, keyword = "") => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/listDoctors/${entityId}`,
        {
          params: { page, limit, keyword },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
 
      const doctors = response.data.data.doctors || [];
      const totalItems = response.data.data.total || 0;
      // Map the data if needed (example: you can rename fields)
      const mappedDoctors = doctors.map((doctor) => ({
        id: doctor._id,
        name: doctor.name, 
        clinicname: doctor.clinics?.[0]?.name || "NA",
        totalScans: doctor.totalScans || 0,
      }));

      return { doctors: mappedDoctors, totalItems };
    } catch (error) {
      console.error("Error fetching clinics:", error);
      throw error;
    }
  },
  fetchDoctorDetails: async (doctorId, token) => {
    if (!doctorId) {
      throw new Error("Doctor ID is required to fetch details");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/doctors/${doctorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("resp from doc",response);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchUsers: async (auth, entityId, page = 1, limit = 10, doctorIds ,clinicIds,keyword = "",testStartDate,testEndDate,sourceFilter) => {
    try { 
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/listPatients/${entityId}`,
        {
          params: { page, limit, doctorIds ,clinicIds,keyword,testStartDate,testEndDate,sourceFilter },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
     
      const users = response.data.data.data || []; 
      const totalItems = response.data.data.filteredItemsCount || 0;
      // Map the data if needed (example: you can rename fields)
      const mappedClinics = users.map((user) => ({
        id: user.uid,
        name: user.name, 
        mobile: user.mobile,
        lastTestDate: user.lastTestDate,
        testDate:user.testDate,
        doctor:user.doctor
    
      }));
     console.log("mappedClinics",mappedClinics);
      return { mapped: mappedClinics, totalItems };
    } catch (error) {
      console.error("Error fetching clinics:", error);
      throw error;
    }
  },
  exportPatientsData: async (
    auth,
    entityId,
    clinicIds = [],
    startDate,
    endDate,
    includeDiseases = false
  ) => {
    try {
      // The backend only returns the filtered patient rows as JSON; the xlsx
      // file itself is built here on the frontend.
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/exportPatientsData/${entityId}`,
        {
          params: {
            clinicIds: JSON.stringify(clinicIds),
            startDate,
            endDate,
            includeDiseases,
          },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );

      const { headers = [], rows = [] } = response.data?.data || {};
      const workbookArrayBuffer = await buildPatientsWorkbookBuffer(headers, rows);

      const blob = new Blob([workbookArrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = `patients_export_${Date.now()}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("Error exporting patients data:", error);
      throw error;
    }
  },
  fetchDoctorAndClinics: async (entityId, doctorKeyword, clinicKeyword) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/getAllDoctorsAndClinic/${entityId}`,
        {
          params: { doctorKeyword, clinicKeyword },
          //headers: { Authorization: `Bearer ${auth}` },
        }
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching doctos and clinics for dropdown", error);
      throw error;
    }
  },

  fetchUserTests: async (userId, auth, page, limit, keyword) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/getUserTests`,
        {
          params: { userId, page, limit, keyword },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      return response;
    } catch (error) {
      console.error("Error fetching tests:", error);
      throw error;
    }
  },
  getImage: async (auth, url) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/getImageFromUrl`,
        {
          headers: { Authorization: `Bearer ${auth}` },
          params: { url }, // query param
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching image:", error);
      throw error;
    }
  },
  getCounts: async (auth,entityId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/admin/getAllCounts/${entityId}`,
        {
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching counts:", error);
      throw error;
    }
  },
  getDayWiseData: async (auth, startDate, endDate, entityId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/analytics/getDayWiseOverall/${entityId}`,
        {
          params: { startDate, endDate },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      return response;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  },
  getOverAllMonthwise: async (auth, startDate, endDate,entityId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/enterprise/v1/analytics/getMonthWiseOverall/${entityId}`,
        {
          params: { startDate, endDate },
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      return response;
    } catch (error) {
      console.error("Error fetching image:", error);
      throw error;
    }
  },

  
};

export default apiService;
